import { Link } from "react-router-dom";
import {
  ArrowLeft, Buildings, Compass, ShieldStar, ChartLine, Package, Envelope,
  User, Certificate,
} from "@phosphor-icons/react";
import SresLogo from "@/components/SresLogo";
import { aboutImageUrl } from "@/lib/api";

const ICONS = { Compass, ShieldStar, ChartLine, Package, Buildings, User, Certificate };

/**
 * Renders the About marketing page from a content object.
 * Same component powers `/about` (fed by API) and the CMS live-preview panel
 * (fed by unsaved form state).
 *
 * Props:
 *   data — the full about content object
 *   compact — reduces hero size and paddings, hides external navigation
 */
export default function AboutView({ data = {}, compact = false }) {
  const c = data || {};
  const wrapPad = compact ? "px-4" : "px-6";
  const heroPad = compact ? "pt-8 pb-6" : "pt-20 pb-10";
  const heroTitleCls = compact
    ? "font-heading font-black text-2xl leading-tight max-w-4xl"
    : "font-heading font-black text-5xl md:text-6xl lg:text-7xl leading-none max-w-4xl";
  const sectionTitleCls = compact
    ? "font-heading text-lg font-black mb-4"
    : "font-heading text-3xl md:text-4xl font-black mb-10";
  const cardPad = compact ? "p-3" : "p-6";
  const gridCols = compact ? "grid-cols-1" : "md:grid-cols-2";
  const teamGrid = compact ? "grid-cols-1" : "md:grid-cols-3";

  return (
    <div className={compact ? "text-foreground" : "min-h-screen bg-obsidian text-foreground"} data-testid="about-page">
      {/* Top bar (hidden in compact/preview) */}
      {!compact && (
        <header className="border-b border-border bg-obsidian">
          <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
            <SresLogo size={38} withText />
            <Link
              to="/login"
              className="text-[11px] uppercase tracking-widest text-copper hover:text-copper-hover flex items-center gap-1"
              data-testid="about-back"
            >
              <ArrowLeft size={12} weight="bold" /> Back to Sign in
            </Link>
          </div>
        </header>
      )}

      {/* Hero */}
      <section className={`max-w-6xl mx-auto ${wrapPad} ${heroPad}`}>
        <div className="text-[10px] uppercase tracking-[0.4em] text-copper mb-4" data-testid="about-eyebrow">
          {c.hero_eyebrow || "Company"}
        </div>
        <h1 className={heroTitleCls} data-testid="about-title">
          {c.hero_title || "Mining intelligence, engineered for evidence."}
        </h1>
        <p
          className={`text-muted-foreground max-w-2xl ${compact ? "mt-3 text-sm" : "mt-6 text-base leading-relaxed"}`}
          data-testid="about-body"
        >
          {c.hero_body}
        </p>
        {(c.stats?.length ?? 0) > 0 && (
          <div className={`${compact ? "mt-4 gap-x-6 gap-y-2 text-[9px]" : "mt-10 gap-x-10 gap-y-4 text-[11px]"} flex flex-wrap uppercase tracking-widest text-muted-foreground`} data-testid="about-stats">
            {c.stats.map((s, i) => (
              <div key={i}>
                <span className={`text-foreground font-bold ${compact ? "text-sm" : "text-lg"} tabular block`}>{s.value}</span>
                {s.label}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* SRES highlight (hidden in compact to keep preview focused) */}
      {!compact && (
        <section className={`max-w-6xl mx-auto ${wrapPad} py-14`}>
          <div className="border border-border rounded-sm p-8 md:p-10 bg-obsidian flex flex-col md:flex-row md:items-center gap-8">
            <SresLogo size={72} />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.35em] text-copper mb-2">Our flagship platform</div>
              <h2 className="font-heading text-3xl md:text-4xl font-black leading-tight">
                SRES · Shanvi Resource Estimation Studio
              </h2>
              <p className="text-muted-foreground mt-3 max-w-2xl text-sm leading-relaxed">
                SRES is our end-to-end resource-estimation web workspace. It ingests standard
                exploration CSVs, builds regularised 3D block models with domained estimation,
                runs pit optimisation, and ships a 45-page Competent Person Report with
                AI-authored narrative — bookmarked, watermarked and shareable via public preview link.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 mt-6 h-10 px-5 rounded-sm bg-copper hover:bg-copper-hover text-white font-bold uppercase tracking-widest text-[11px] transition-colors"
                data-testid="about-open-sres"
              >
                Open the studio
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Capabilities */}
      {(c.capabilities?.length ?? 0) > 0 && (
        <section className={`max-w-6xl mx-auto ${wrapPad} py-6`} data-testid="about-capabilities">
          <div className="text-[10px] uppercase tracking-[0.35em] text-copper mb-3">Capabilities</div>
          <h3 className={sectionTitleCls}>Four practices, one accountable engineering partner.</h3>
          <div className={`grid ${gridCols} gap-3`}>
            {c.capabilities.map((cap, i) => {
              const Icon = ICONS[cap.icon] || ChartLine;
              return (
                <div key={i} className={`border border-border rounded-sm ${cardPad} bg-obsidian hover:border-copper/40 transition-colors`}>
                  <Icon size={compact ? 20 : 26} className="text-copper mb-3" weight="duotone" />
                  <div className={`font-heading ${compact ? "text-sm" : "text-lg"} font-bold mb-1`}>{cap.title}</div>
                  <p className={`text-muted-foreground ${compact ? "text-xs" : "text-sm"} leading-relaxed`}>{cap.body}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Team */}
      {(c.team?.length ?? 0) > 0 && (
        <section className={`max-w-6xl mx-auto ${wrapPad} py-6`} data-testid="about-team">
          <div className="text-[10px] uppercase tracking-[0.35em] text-copper mb-3">Team</div>
          <h3 className={sectionTitleCls}>The people signing your report.</h3>
          <div className={`grid ${teamGrid} gap-3`}>
            {c.team.map((m, i) => (
              <div key={i} className={`border border-border rounded-sm ${cardPad} bg-obsidian`}>
                {m.image_path ? (
                  <img
                    src={aboutImageUrl(m.image_path)}
                    alt={m.name}
                    className={`${compact ? "w-10 h-10" : "w-14 h-14"} rounded-full object-cover border border-copper/40 mb-3`}
                    loading="lazy"
                  />
                ) : (
                  <div className={`${compact ? "w-10 h-10 text-sm" : "w-14 h-14 text-lg"} rounded-full bg-copper/15 border border-copper/40 flex items-center justify-center text-copper font-heading font-black tracking-wider mb-3`}>
                    {m.initials || (m.name || "").split(/\s+/).map(x => x[0]).slice(0, 2).join("")}
                  </div>
                )}
                <div className={`font-heading ${compact ? "text-sm" : "text-lg"} font-bold`}>{m.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-copper/80 mt-0.5">{m.role}</div>
                {m.bio && <p className={`text-muted-foreground ${compact ? "text-xs" : "text-sm"} leading-relaxed mt-2`}>{m.bio}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Case studies */}
      {(c.case_studies?.length ?? 0) > 0 && (
        <section className={`max-w-6xl mx-auto ${wrapPad} py-6`} data-testid="about-cases">
          <div className="text-[10px] uppercase tracking-[0.35em] text-copper mb-3">Selected work</div>
          <h3 className={sectionTitleCls}>Field-proven, code-aligned deliveries.</h3>
          <div className={`grid ${gridCols} gap-3`}>
            {c.case_studies.map((cs, i) => (
              <div key={i} className="border border-border rounded-sm bg-obsidian overflow-hidden">
                {cs.image_path && (
                  <img
                    src={aboutImageUrl(cs.image_path)}
                    alt={cs.title}
                    className={`w-full ${compact ? "h-24" : "h-40"} object-cover border-b border-border`}
                    loading="lazy"
                  />
                )}
                <div className={cardPad}>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {cs.commodity && <span className="text-[10px] uppercase tracking-widest bg-copper/15 text-copper px-2 py-0.5 rounded-sm">{cs.commodity}</span>}
                    {cs.region && <span className="text-[10px] uppercase tracking-widest border border-border text-muted-foreground px-2 py-0.5 rounded-sm">{cs.region}</span>}
                  </div>
                  <div className={`font-heading ${compact ? "text-sm" : "text-lg"} font-bold mb-1`}>{cs.title}</div>
                  <p className={`text-muted-foreground ${compact ? "text-xs" : "text-sm"} leading-relaxed`}>{cs.summary}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Contact */}
      <section className={`max-w-6xl mx-auto ${wrapPad} ${compact ? "py-4" : "py-16"}`}>
        <div className={`border border-border rounded-sm ${compact ? "p-4" : "p-8 md:p-10"} flex flex-col md:flex-row md:items-center md:justify-between gap-4`}>
          <div className="flex items-start gap-3">
            <Buildings size={compact ? 20 : 28} className="text-copper mt-1" weight="duotone" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Contact</div>
              <div className={`font-heading ${compact ? "text-base" : "text-xl"} font-bold mt-1`} data-testid="about-contact-org">{c.contact_org}</div>
              <div className={`text-muted-foreground ${compact ? "text-xs" : "text-sm"} mt-1`} data-testid="about-contact-desc">{c.contact_desc}</div>
            </div>
          </div>
          <a
            href={`mailto:${c.contact_email || "info@sresources.co"}`}
            className={`inline-flex items-center gap-2 ${compact ? "h-8 px-3 text-[9px]" : "h-10 px-5 text-[11px]"} rounded-sm border border-copper/40 bg-copper/10 text-copper hover:bg-copper/20 uppercase tracking-widest font-bold transition-colors`}
            data-testid="about-contact-email"
          >
            <Envelope size={14} weight="bold" /> {c.contact_email || "info@sresources.co"}
          </a>
        </div>
      </section>

      {/* Footer (hidden in compact) */}
      {!compact && (
        <footer className="border-t border-border">
          <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-2 text-[10px] uppercase tracking-widest text-muted-foreground mono">
            <div>© {new Date().getFullYear()} Shanvi Resources · All rights reserved</div>
            <div className="text-copper/70">SRES · Shanvi Resource Estimation Studio</div>
          </div>
        </footer>
      )}
    </div>
  );
}
